import InputLabelField from '@/components/form-control/InputLabelField'
import SelectLabel from '@/components/form-control/SelectLabel'
import {
  DATA_TABLE_SENSORS,
  LIST_AREA,
  LIST_CONFIGURATION,
  LIST_CONTROLLER_SENSOR,
  LIST_GATEWAY,
  LIST_MODEL,
  LIST_SITE,
  LIST_TYPE_SENSOR,
  LIST_VENDOR,
} from '@/constant/common'

import { useConfigUI } from '@/features/config/context'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Container,
  FormControl,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import * as yup from 'yup'
import useTranslation from '@/hooks/useTranslation'

interface SensorViewProps {
  id?: string
}

const schema = yup
  .object({
    address: yup.string().required('Address is required'),
    description: yup.string().required('Description is required'),
  })
  .required()

const SensorView = ({ id }: SensorViewProps) => {
  const { t } = useTranslation()
  const { closeSidebar } = useConfigUI()
  const [gateway, setGateway] = useState('')
  const [configuration, setConfiguration] = useState('')
  const [type, setType] = useState('')
  const [site, setSite] = useState('')
  const [area, setArea] = useState('')
  const [controller, setController] = useState('')
  const [vendor, setVendor] = useState('')
  const [model, setModel] = useState('')

  const data = DATA_TABLE_SENSORS.find((item) => item.id === id)

  const inputRefSiteName = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  const handleGateway = (event: SelectChangeEvent) => {
    setGateway(event.target.value)
  }

  const handleConfiguration = (event: SelectChangeEvent) => {
    setConfiguration(event.target.value)
  }

  const handleType = (event: SelectChangeEvent) => {
    setType(event.target.value)
  }

  const handleSite = (event: SelectChangeEvent) => {
    setSite(event.target.value)
  }

  const handleArea = (event: SelectChangeEvent) => {
    setArea(event.target.value)
  }

  const handleController = (event: SelectChangeEvent) => {
    setController(event.target.value)
  }

  const handleVendor = (event: SelectChangeEvent) => {
    setVendor(event.target.value)
  }

  const handleModel = (event: SelectChangeEvent) => {
    setModel(event.target.value)
  }

  const form = useForm({
    defaultValues: {
      address: '',
      description: '',
      ...data,
    },
    resolver: yupResolver(schema),
  })

  const handleSave = () => {
    try {
      closeSidebar()
      toast.success(t('addSensorSuccess'))
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        console.error('error', error)
      }
    }
  }

  return (
    <Container sx={{ maxWidth: '450px' }}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <Stack sx={{ padding: '34px 18px' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '377px' }}
          >
            <Typography
              sx={{
                fontWeight: '500',
                lineHeight: '14px',
                color: '#000000',
              }}
            >
              {id ? t('EditSensor') : t('AddSensor')}
            </Typography>
            <Stack direction="row">
              <Button
                color="secondary"
                sx={{ marginRight: '7px', width: '85px' }}
                onClick={() => closeSidebar()}
              >
                {t('commonActionsCancel')}
              </Button>
              <Button color="primary" sx={{ minWidth: '72px' }} type="submit">
                {t('commonActionsSave')}
              </Button>
            </Stack>
          </Stack>

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '22px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Input/Gateway"
                  onChange={handleGateway}
                  listItem={LIST_GATEWAY}
                  value={gateway}
                />
              </Stack>
            </FormControl>
          </Stack>

          <InputLabelField
            name="address"
            form={form}
            label={t('formsLabelsAddress')}
            placeholder={t('formsLabelsAddress')}
            inputRef={inputRefSiteName}
            style={{
              height: 50,
              borderRadius: '4px',
              width: { xs: '377px' },
              marginTop: '8px',
            }}
            padding="8px 16px"
          />

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Configuration"
                  onChange={handleConfiguration}
                  listItem={LIST_CONFIGURATION}
                  value={configuration}
                />
              </Stack>
            </FormControl>
          </Stack>

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Type"
                  onChange={handleType}
                  listItem={LIST_TYPE_SENSOR}
                  value={type}
                />
              </Stack>
            </FormControl>
          </Stack>

          <InputLabelField
            name="description"
            form={form}
            label={t('commonLabelsDescription')}
            placeholder={t('ThisIsAText')}
            style={{
              height: 50,
              borderRadius: '4px',
              width: { xs: '377px' },
              marginTop: '8px',
            }}
            padding="8px 16px"
          />

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Site"
                  onChange={handleSite}
                  listItem={LIST_SITE}
                  value={site}
                />
              </Stack>
            </FormControl>
          </Stack>

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Area"
                  onChange={handleArea}
                  listItem={LIST_AREA}
                  value={area}
                />
              </Stack>
            </FormControl>
          </Stack>

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Decoder"
                  onChange={handleController}
                  listItem={LIST_CONTROLLER_SENSOR}
                  value={controller}
                />
              </Stack>
            </FormControl>
          </Stack>

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Make(Vendor)"
                  onChange={handleVendor}
                  listItem={LIST_VENDOR}
                  value={vendor}
                />
              </Stack>
            </FormControl>
          </Stack>

          <Stack>
            <FormControl
              variant="standard"
              sx={{
                minWidth: '377px',
                minHeight: '50px',
                marginTop: '8px',
                backgroundColor: 'background.default',
                borderRadius: '4px',
              }}
            >
              <Stack sx={{ padding: '8px 12px' }}>
                <SelectLabel
                  name="Model"
                  onChange={handleModel}
                  listItem={LIST_MODEL}
                  value={model}
                />
              </Stack>
            </FormControl>
          </Stack>
        </Stack>
      </form>
    </Container>
  )
}

export default SensorView
